import React, { useState } from 'react';
import { IoPerson, IoTrash, IoCreate, IoSparkles, IoReorderThree, IoSend } from 'react-icons/io5';
import { Activity } from '@/services/activityService';
import MissingDataIndicator from '@/components/MissingDataIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './ActivityCardsDisplay.css';

interface ActivityCardsDisplayProps {
  activities: Activity[];
  onEdit?: (activity: Activity) => void;
  onDelete?: (activityId: number) => void;
  onAnalyze?: (description: string) => Promise<void>;
  onReorder?: (activities: Activity[]) => Promise<void>;
  isLoading?: boolean;
  isAnalyzing?: boolean;
  missingInfo?: string[];
}

interface EditActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Activity) => void;
  activity: Activity | null;
}

const ACTIVITY_CATEGORIES = [
  "Athletics: Club", "Athletics: JV/Varsity", "Career Oriented", "Community Service (Volunteer)",
  "Computer/Technology", "Cultural", "Dance", "Debate/Speech", "Environmental", 
  "Family Responsibilities", "Foreign Exchange", "Foreign Language", "Internship",
  "Journalism/Publication", "Junior R.O.T.C.", "LGBT", "Music: Instrumental", 
  "Music: Vocal", "Religious", "Research", "Robotics", "School Spirit", 
  "Science/Math", "Social Justice", "Student Govt./Politics", "Theater/Drama", 
  "Work (Paid)", "Other Club/Activity"
];

const GRADE_LEVELS = ["9", "10", "11", "12", "post-graduate"];
const PARTICIPATION_TIMING = [
  { value: "during_school_year", label: "During School Year" },
  { value: "during_school_break", label: "During School Break" },
  { value: "all_year", label: "All Year" }
];

interface SortableActivityCardProps {
  activity: Activity;
  onEdit: (activity: Activity) => void;
  onDelete?: (activityId: number) => void;
  countMissingFields: (activity: Activity) => number;
  capitalizePosition: (position: string) => string;
  formatGrades: (grades: string[]) => string;
  formatTiming: (timing: string) => string;
}

const SortableActivityCard: React.FC<SortableActivityCardProps> = ({
  activity,
  onEdit,
  onDelete,
  countMissingFields,
  capitalizePosition,
  formatGrades,
  formatTiming
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: activity.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const line2Parts = [];
  
  const gradesText = formatGrades(activity.participation_grades);
  if (gradesText) {
    line2Parts.push(
      <span key="grades" className="activity-card-compact-grades">{gradesText}</span>
    );
  }
  
  if (activity.hours_per_week !== null && activity.hours_per_week > 0) {
    if (line2Parts.length > 0) {
      line2Parts.push(<span key="sep1" className="activity-card-compact-separator">•</span>);
    }
    line2Parts.push(
      <span key="hours" className="activity-card-compact-time">{activity.hours_per_week}h/week</span>
    );
  }
  
  if (activity.weeks_per_year !== null && activity.weeks_per_year > 0) {
    if (line2Parts.length > 0) {
      line2Parts.push(<span key="sep2" className="activity-card-compact-separator">•</span>);
    }
    line2Parts.push(
      <span key="weeks" className="activity-card-compact-weeks">{activity.weeks_per_year} weeks/year</span>
    );
  }
  
  if (activity.timing_of_participation) {
    if (line2Parts.length > 0) {
      line2Parts.push(<span key="sep3" className="activity-card-compact-separator">•</span>);
    }
    line2Parts.push(
      <span key="timing" className="activity-card-compact-timing">{formatTiming(activity.timing_of_participation)}</span>
    );
  }
  
  if (activity.intend_to_participate_in_college) {
    if (line2Parts.length > 0) {
      line2Parts.push(<span key="sep4" className="activity-card-compact-separator">•</span>);
    }
    line2Parts.push(
      <span key="college" className="activity-card-compact-college">Continue in college</span>
    );
  }

  const missingFieldsCount = countMissingFields(activity);

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`activity-card-compact ${isDragging ? 'dragging' : ''}`}
    >
      <div className="activity-card-compact-main">
        <div 
          className="activity-card-drag-handle"
          {...attributes}
          {...listeners}
          title="Drag to reorder"
        >
          <IoReorderThree />
        </div>

        <div 
          className="activity-card-compact-content"
          onClick={() => onEdit(activity)}
          style={{ cursor: 'pointer' }}
          title="Click to edit activity details"
        >
          <div className="activity-card-compact-line1">
            <span className="activity-card-compact-position">
              {activity.position_description ? capitalizePosition(activity.position_description) : 'Position not specified'}
            </span>
            {activity.organization_name && (
              <>
                <span className="activity-card-compact-separator">•</span>
                <span className="activity-card-compact-organization">{activity.organization_name}</span>
              </>
            )}
            <span className="activity-card-compact-separator">•</span>
            <span className="activity-card-compact-type">{activity.activity_type}</span>
          </div>
          <div className="activity-card-compact-line2">
            {line2Parts.length > 0 ? line2Parts : (
              <span className="activity-card-compact-placeholder">Click to add details</span>
            )}
          </div>
        </div>
        
        <div className="activity-card-compact-actions" onClick={(e) => e.stopPropagation()}>
          {missingFieldsCount > 0 && (
            <MissingDataIndicator 
              missingCount={missingFieldsCount}
              onClick={() => onEdit(activity)}
              className="inline"
            />
          )}
          <button 
            onClick={() => onEdit(activity)}
            className="activity-card-compact-action"
            title="Edit activity"
          >
            <IoCreate />
          </button>
          {onDelete && (
            <button
              onClick={() => {
                if (window.confirm('Delete this activity? This cannot be undone.')) {
                  onDelete(activity.id);
                }
              }}
              className="activity-card-compact-action activity-card-compact-delete"
              title="Delete activity"
            >
              <IoTrash />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EditActivityModal: React.FC<EditActivityModalProps> = ({ isOpen, onClose, onSave, activity }) => {
  const [formData, setFormData] = useState<Activity>({
    id: 0,
    user_id: 0,
    activity_type: '',
    position_description: '',
    organization_name: '',
    activity_description: '',
    participation_grades: [],
    timing_of_participation: 'during_school_year',
    hours_per_week: null,
    weeks_per_year: null,
    intend_to_participate_in_college: false,
    confidence_score: 0,
    order_index: 0
  });

  React.useEffect(() => {
    if (activity && isOpen) {
      setFormData({ ...activity });
    }
  }, [activity, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const handleGradeChange = (grade: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        participation_grades: [...prev.participation_grades, grade]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        participation_grades: prev.participation_grades.filter(g => g !== grade)
      }));
    }
  };

  if (!isOpen || !activity) return null;

  return (
    <div className="activity-modal-overlay">
      <div className="activity-modal">
        <div className="activity-modal-header">
          <h3>Edit Activity</h3>
          <button onClick={onClose} className="activity-modal-close">×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="activity-modal-form">
          <div className="activity-modal-field">
            <label>Activity Type</label>
            <select 
              value={formData.activity_type}
              onChange={(e) => setFormData({...formData, activity_type: e.target.value})}
              required
            >
              <option value="">Select activity type</option>
              {ACTIVITY_CATEGORIES.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="activity-modal-field">
            <label>Position/Leadership Description (Max 50 chars)</label>
            <input 
              type="text" 
              value={formData.position_description}
              onChange={(e) => setFormData({...formData, position_description: e.target.value.slice(0, 50)})}
              maxLength={50}
              required
            />
            <div className="activity-modal-char-count">{formData.position_description.length}/50</div>
          </div>

          <div className="activity-modal-field">
            <label>
              Organization Name (Max 100 chars)
              {(!formData.organization_name || formData.organization_name.trim() === '') && (
                <span className="activity-modal-missing-indicator">• Missing</span>
              )}
            </label>
            <input 
              type="text" 
              value={formData.organization_name || ''}
              onChange={(e) => setFormData({...formData, organization_name: e.target.value.slice(0, 100)})}
              maxLength={100}
            />
            <div className="activity-modal-char-count">{(formData.organization_name || '').length}/100</div>
          </div>

          <div className="activity-modal-field">
            <label>
              Activity Description (Max 150 chars)
              {(!formData.activity_description || formData.activity_description.trim() === '') && (
                <span className="activity-modal-missing-indicator">• Missing</span>
              )}
            </label>
            <textarea 
              value={formData.activity_description}
              onChange={(e) => setFormData({...formData, activity_description: e.target.value.slice(0, 150)})}
              maxLength={150}
              rows={3}
              required
            />
            <div className="activity-modal-char-count">{formData.activity_description.length}/150</div>
          </div>

          <div className="activity-modal-field">
            <label>Participation Grade Levels</label>
            <div className="activity-modal-checkbox-grid">
              {GRADE_LEVELS.map(grade => (
                <label key={grade} className="activity-modal-checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={formData.participation_grades.includes(grade)}
                    onChange={(e) => handleGradeChange(grade, e.target.checked)}
                  />
                  <span>Grade {grade === 'post-graduate' ? 'Post-Grad' : grade}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="activity-modal-field">
            <label>Timing of Participation</label>
            <select 
              value={formData.timing_of_participation}
              onChange={(e) => setFormData({...formData, timing_of_participation: e.target.value})}
              required
            >
              {PARTICIPATION_TIMING.map(timing => (
                <option key={timing.value} value={timing.value}>{timing.label}</option>
              ))}
            </select>
          </div>

          <div className="activity-modal-row">
            <div className="activity-modal-field">
              <label>
                Hours per Week
                {(!formData.hours_per_week || formData.hours_per_week <= 0) && (
                  <span className="activity-modal-missing-indicator">• Missing</span>
                )}
              </label>
              <input 
                type="number" 
                value={formData.hours_per_week || ''}
                onChange={(e) => setFormData({...formData, hours_per_week: parseInt(e.target.value) || null})}
                min="1"
                max="168"
                placeholder="Enter hours per week"
              />
            </div>
            <div className="activity-modal-field">
              <label>
                Weeks per Year
                {(!formData.weeks_per_year || formData.weeks_per_year <= 0) && (
                  <span className="activity-modal-missing-indicator">• Missing</span>
                )}
              </label>
              <input 
                type="number" 
                value={formData.weeks_per_year || ''}
                onChange={(e) => setFormData({...formData, weeks_per_year: parseInt(e.target.value) || null})}
                min="1"
                max="52"
                placeholder="Enter weeks per year"
              />
            </div>
          </div>

          <div className="activity-modal-field">
            <label className="activity-modal-checkbox-label">
              <input 
                type="checkbox" 
                checked={formData.intend_to_participate_in_college}
                onChange={(e) => setFormData({...formData, intend_to_participate_in_college: e.target.checked})}
              />
              <span>I intend to participate in a similar activity in college</span>
            </label>
          </div>

          <div className="activity-modal-actions">
            <button type="button" onClick={onClose} className="activity-modal-cancel">
              Cancel
            </button>
            <button type="submit" className="activity-modal-submit">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DescribeActivityCard: React.FC<{
  onAnalyze: (description: string) => Promise<void>;
  isAnalyzing: boolean;
}> = ({ onAnalyze, isAnalyzing }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const maxChars = 1000;

  const handleAnalyze = async () => {
    if (!text.trim()) {
      setError('Please describe your activity');
      return;
    }
    try {
      setError('');
      await onAnalyze(text.trim());
      setText('');
    } catch (err) {
      setError('Failed to analyze activity. Please try again.');
    }
  };

  return (
    <div className="activity-describe-card">
      <div className="activity-describe-header">
        <div className="activity-describe-icon"><IoSparkles /></div>
        <div className="activity-describe-header-text">
          <h3 className="activity-describe-title">Describe Your Activity</h3>
          <p className="activity-describe-sub">Tell us about an extracurricular activity, and our AI will help organize the details for your college applications.</p>
        </div>
      </div>

      <div className="activity-describe-textarea-wrapper">
        <textarea
          className={`activity-describe-textarea ${error ? 'error' : ''}`}
          placeholder="Example: I was captain of the varsity soccer team for two years. I led the team to win the state championship in my senior year and organized weekly community service events where we coached younger kids. I practiced 15 hours per week during the season and plan to play intramural soccer in college..."
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, maxChars))}
          rows={5}
          disabled={isAnalyzing}
        />
        <div className="activity-describe-charcount">{text.length}/{maxChars} characters</div>
      </div>

      {error && <div className="activity-describe-error">{error}</div>}

      <button className="activity-describe-button" onClick={handleAnalyze} disabled={isAnalyzing || !text.trim()}>
        {isAnalyzing ? (
          <>
            <div className="activity-describe-spinner" />
            Analyzing Activity...
          </>
        ) : (
          <>
            <IoSend className="activity-describe-button-icon" />
            Analyze Activity
          </>
        )}
      </button>
    </div>
  );
};

const TipsBox: React.FC = () => (
  <div className="activity-tips">
    <div className="activity-tips-inner">
      <h4 className="activity-tips-title">💡 Tips for better results:</h4>
      <ul className="activity-tips-list">
        <li>Include your role or position (captain, president, volunteer, etc.)</li>
        <li>Mention the organization or group name</li>
        <li>Describe what you accomplished or any recognition received</li>
        <li>Include time commitment (hours per week, which years)</li>
        <li>Note if you plan to continue this activity in college</li>
      </ul>
    </div>
  </div>
);

const CompactActivityInput: React.FC<{
  onAnalyze: (description: string) => Promise<void>;
  isAnalyzing: boolean;
}> = ({ onAnalyze, isAnalyzing }) => {
  const [text, setText] = useState('');

  const handleSubmit = async () => {
    if (!text.trim() || isAnalyzing) return;
    try {
      await onAnalyze(text.trim());
      setText('');
    } catch {}
  };

  return (
    <div className="compact-activity-input">
      <div className="compact-activity-input-icon"><IoSparkles /></div>
      <input
        type="text"
        className="compact-activity-input-field"
        placeholder="Describe another activity (e.g., I was president of debate club...)"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
        disabled={isAnalyzing}
        maxLength={1000}
      />
      <button
        className="compact-activity-input-submit"
        onClick={handleSubmit}
        disabled={isAnalyzing || !text.trim()}
        title="Analyze activity"
      >
        {isAnalyzing
          ? <div className="compact-activity-spinner" />
          : <IoSend />
        }
      </button>
    </div>
  );
};

const ActivityCardsDisplay: React.FC<ActivityCardsDisplayProps> = ({
  activities,
  onEdit,
  onDelete,
  onAnalyze,
  onReorder,
  isLoading = false,
  isAnalyzing = false,
  missingInfo = [],
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [localActivities, setLocalActivities] = useState<Activity[]>(activities);

  React.useEffect(() => {
    setLocalActivities(activities);
  }, [activities]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const countMissingFields = (activity: Activity): number => {
    let missingCount = 0;
    
    if (!activity.hours_per_week || activity.hours_per_week <= 0) missingCount++;
    if (!activity.weeks_per_year || activity.weeks_per_year <= 0) missingCount++;
    if (!activity.organization_name || activity.organization_name.trim() === '') missingCount++;
    if (!activity.activity_description || activity.activity_description.trim() === '') missingCount++;
    
    return missingCount;
  };

  const capitalizePosition = (position: string): string => {
    if (!position) return position;
    return position
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
    setShowEditModal(true);
  };

  const handleSaveActivity = (activity: Activity) => {
    if (onEdit) {
      onEdit(activity);
    }
    setEditingActivity(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = localActivities.findIndex((item) => item.id === active.id);
      const newIndex = localActivities.findIndex((item) => item.id === over?.id);

      const newActivities = arrayMove(localActivities, oldIndex, newIndex);
      
      setLocalActivities(newActivities);

      const reorderedActivities = newActivities.map((activity, index) => ({
        ...activity,
        order_index: index
      }));

      if (onReorder) {
        try {
          await onReorder(reorderedActivities);
        } catch (error) {
          console.error('Failed to reorder activities:', error);
          setLocalActivities(activities);
        }
      }
    }
  };

  const formatGrades = (grades: string[]) => {
    if (!grades || grades.length === 0) return '';
    
    const sortedGrades = grades.filter(g => g !== 'post-graduate').sort((a, b) => parseInt(a) - parseInt(b));
    const hasPostGrad = grades.includes('post-graduate');
    
    if (sortedGrades.length === 0 && hasPostGrad) return 'Post-Graduate';
    if (sortedGrades.length === 1 && !hasPostGrad) return `Grade ${sortedGrades[0]}`;
    
    if (sortedGrades.length > 1) {
      const first = parseInt(sortedGrades[0]);
      const last = parseInt(sortedGrades[sortedGrades.length - 1]);
      
      const isConsecutive = sortedGrades.every((grade, index) => 
        index === 0 || parseInt(grade) === parseInt(sortedGrades[index - 1]) + 1
      );
      
      if (isConsecutive && sortedGrades.length > 2) {
        const result = `Grades ${first}-${last}`;
        return hasPostGrad ? `${result}, Post-Graduate` : result;
      }
    }
    
    const gradeList = sortedGrades.map(grade => `Grade ${grade}`);
    if (hasPostGrad) gradeList.push('Post-Graduate');
    return gradeList.join(', ');
  };

  const formatTiming = (timing: string) => {
    const timingMap = {
      'during_school_year': 'School Year',
      'during_school_break': 'School Break',
      'all_year': 'All Year'
    };
    return timingMap[timing as keyof typeof timingMap] || timing;
  };

  if (isLoading) {
    return (
      <div className="activity-cards-display">
        <div className="activity-skeleton-card" />
        <div className="activity-skeleton-card activity-skeleton-card--short" />
        <div className="activity-skeleton-card activity-skeleton-card--short" />
      </div>
    );
  }

  if (localActivities.length === 0) {
    return (
      <div className="activity-cards-display">
        {onAnalyze ? (
          <>
            <DescribeActivityCard onAnalyze={onAnalyze} isAnalyzing={isAnalyzing} />
            <TipsBox />
          </>
        ) : (
          <div className="activity-cards-empty">
            <div className="activity-cards-empty-content">
              <IoPerson className="activity-cards-empty-icon" />
              <h4 className="activity-cards-empty-title">No Activities Added Yet</h4>
              <p className="activity-cards-empty-text">
                Start by describing your first extracurricular activity above.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="activity-cards-display">
      {onAnalyze && (
        <CompactActivityInput onAnalyze={onAnalyze} isAnalyzing={isAnalyzing} />
      )}

      <div className="activity-cards-header">
        <h4 className="activity-cards-title">Your Activities ({localActivities.length})</h4>
        <p className="activity-cards-subtitle">
          Drag activities to order them from most important to least important
        </p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={localActivities.map(activity => activity.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="activity-cards-list">
            {localActivities.map((activity) => (
              <SortableActivityCard
                key={activity.id}
                activity={activity}
                onEdit={handleEditActivity}
                onDelete={onDelete}
                countMissingFields={countMissingFields}
                capitalizePosition={capitalizePosition}
                formatGrades={formatGrades}
                formatTiming={formatTiming}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {missingInfo.length > 0 && (
        <div className="activity-suggestions">
          <div className="activity-suggestions-inner">
            <h4 className="activity-suggestions-title">💡 Suggestions to improve your activities:</h4>
            <ul className="activity-suggestions-list">
              {missingInfo.map((suggestion, i) => (
                <li key={i}>{suggestion}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <EditActivityModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveActivity}
        activity={editingActivity}
      />
    </div>
  );
};

export default ActivityCardsDisplay;